import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';


const STATUS_STYLES = {
  Submitted: 'status-submitted',
  'In Review': 'status-review',
  Analyzing: 'status-analyzing',
  'Report Ready': 'status-ready',
  'Report Sent': 'status-sent',
  Completed: 'status-completed',
};

const SAMPLE_REPORT = {
  summary: 'This application has been analyzed for migration readiness. Below is the preliminary assessment based on the DXL structure, forms, views, agents, and security model.',
  complexity: 'Medium',
  estimatedEffort: '6–8 weeks',
  sections: [
    {
      title: 'Design Elements',
      icon: 'layout',
      items: [
        { label: 'Forms', value: '12 forms detected (3 complex, 9 simple)' },
        { label: 'Views', value: '8 views with categorized columns' },
        { label: 'Pages', value: '4 static pages' },
        { label: 'Subforms', value: '6 shared subforms' },
      ],
    },
    {
      title: 'Business Logic',
      icon: 'cpu',
      items: [
        { label: 'Agents', value: '5 scheduled agents (LotusScript), 3 triggered agents' },
        { label: 'Formula Language', value: 'Moderate use in computed fields and hide-when formulas' },
        { label: 'Workflow', value: 'Approval chain detected (3-step)' },
      ],
    },
    {
      title: 'Security & ACL',
      icon: 'shield',
      items: [
        { label: 'ACL Entries', value: '14 entries across 4 roles' },
        { label: 'Reader/Author Fields', value: '3 forms use reader/author access control' },
        { label: 'Encryption', value: 'No field-level encryption detected' },
      ],
    },
    {
      title: 'Risk Areas',
      icon: 'alert',
      items: [
        { label: 'LotusScript Complexity', value: 'Agent "ProcessApprovals" has 450+ lines — needs refactoring' },
        { label: 'External Calls', value: 'ODBC connection to legacy Oracle DB detected' },
        { label: 'Attachments', value: 'Rich text fields with embedded OLE objects may need special handling' },
      ],
    },
  ],
  recommendation: 'This application is a good candidate for migration to the target platform. We recommend a phased approach: migrate core forms/views first, then replicate agent logic as cloud functions or Power Automate flows. The ODBC integration will require a replacement data connector.',
};

function getSectionIcon(iconName) {
  const props = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'var(--color-primary)', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (iconName) {
    case 'layout':
      return <svg {...props}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
    case 'cpu':
      return <svg {...props}><rect x="4" y="4" width="16" height="16" rx="2" ry="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>;
    case 'shield':
      return <svg {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case 'alert':
      return <svg {...props}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    default:
      return null;
  }
}

// Small inline icon helper for detail labels (14px, matches secondary text color)
const DI = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' };
const DetailIcons = {
  organization: <svg {...DI}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  contact: <svg {...DI}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  email: <svg {...DI}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  platform: <svg {...DI}><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  priority: <svg {...DI}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
  status: <svg {...DI}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  calendar: <svg {...DI}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  users: <svg {...DI}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  documents: <svg {...DI}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  apps: <svg {...DI}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  approach: <svg {...DI}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  description: <svg {...DI}><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>,
  features: <svg {...DI}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  infrastructure: <svg {...DI}><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  integration: <svg {...DI}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  security: <svg {...DI}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  notes: <svg {...DI}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
};

/**
 * Build report data from real analysis results (replaces SAMPLE_REPORT when available)
 */
function buildReportFromAnalysis(sub) {
  const summary = sub.analysisSummary || {};
  const results = sub.analysisResults || {};

  const complexityDist = summary.complexityDistribution || {};
  const complexStr = Object.entries(complexityDist)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${v} ${k}`)
    .join(', ');

  const sections = [
    {
      title: 'Design Elements',
      icon: 'layout',
      items: [
        { label: 'Forms', value: `${summary.totalForms || 0} forms detected${complexStr ? ` (${complexStr})` : ''}` },
        { label: 'Views', value: `${summary.totalViews || 0} views` },
        { label: 'Fields', value: `${summary.totalFields || 0} total fields across all forms` },
        { label: 'Agents', value: `${summary.totalAgents || 0} agents` },
        ...(summary.totalSubforms > 0 ? [{ label: 'Subforms', value: `${summary.totalSubforms} shared subforms` }] : []),
        ...(summary.totalScriptLibraries > 0 ? [{ label: 'Script Libraries', value: `${summary.totalScriptLibraries} libraries` }] : []),
      ],
    },
    {
      title: 'Business Logic',
      icon: 'cpu',
      items: [
        { label: 'Workflows', value: summary.hasWorkflows ? `${summary.workflowCount} workflow(s) detected` : 'No workflows detected' },
        { label: 'Relationships', value: `${summary.totalRelationships || 0} form relationships detected` },
        { label: 'Business Domain', value: `${summary.detectedDomain || 'Unknown'}${summary.domainConfidence ? ` (${Math.round(summary.domainConfidence * 100)}% confidence)` : ''}` },
      ],
    },
    {
      title: 'Security & ACL',
      icon: 'shield',
      items: [
        { label: 'Roles', value: `${summary.roleCount || 0} roles defined` },
        { label: 'Security Risks', value: summary.securityRiskCount > 0 ? `${summary.securityRiskCount} risk(s) identified` : 'No security risks identified' },
      ],
    },
  ];

  // Add risk factors if complexity module found them
  const risks = (results.complexity && results.complexity.riskFactors) || [];
  if (risks.length > 0) {
    sections.push({
      title: 'Risk Areas',
      icon: 'alert',
      items: risks.map(r => ({ label: r.factor || r.risk || 'Risk', value: r.description || r.detail || '' })),
    });
  }

  // Add consolidation data if available
  const consolidation = results.consolidation || {};
  const consolSummary = consolidation.summary || {};
  if (consolSummary.duplicateForms > 0 || consolSummary.consolidationOpportunities > 0) {
    sections.push({
      title: 'Form Consolidation',
      icon: 'layout',
      items: [
        ...(consolSummary.duplicateForms > 0 ? [{ label: 'Duplicate Forms', value: `${consolSummary.duplicateForms} duplicate form pair(s) detected` }] : []),
        ...(consolSummary.consolidationOpportunities > 0 ? [{ label: 'Consolidation Opportunities', value: `${consolSummary.consolidationOpportunities} form pair(s) can be consolidated` }] : []),
        ...(consolSummary.potentialSavings ? [{ label: 'Estimated Savings', value: `${consolSummary.potentialSavings.developmentTimeSaved || '0 days'} development time, ${consolSummary.potentialSavings.maintenanceReduction || '0%'} maintenance reduction` }] : []),
      ],
    });
  }

  // Add migration phase data if available
  const migration = results.migration || {};
  const migSummary = migration.summary || {};
  if (migSummary.phaseCount > 0 || migSummary.multiValueFields > 0 || migSummary.computedFields > 0) {
    const migItems = [
      { label: 'Risk Level', value: `${(migSummary.riskLevel || 'unknown').charAt(0).toUpperCase() + (migSummary.riskLevel || 'unknown').slice(1)}` },
      ...(migSummary.phaseCount > 0 ? [{ label: 'Migration Phases', value: `${migSummary.phaseCount} phase(s) recommended` }] : []),
      ...(migSummary.multiValueFields > 0 ? [{ label: 'Multi-Value Fields', value: `${migSummary.multiValueFields} field(s) requiring normalization` }] : []),
      ...(migSummary.computedFields > 0 ? [{ label: 'Computed Fields', value: `${migSummary.computedFields} field(s) requiring logic migration` }] : []),
      ...(migSummary.technicalDebtScore > 0 ? [{ label: 'Technical Debt', value: `Score: ${migSummary.technicalDebtScore}` }] : []),
    ];
    // Resource estimation
    if (migSummary.estimatedTeamSize) {
      migItems.push({ label: 'Team Size', value: `${migSummary.estimatedTeamSize} person(s) recommended` });
    }
    if (migSummary.estimatedMonthsSequential) {
      migItems.push({ label: 'Timeline (Sequential)', value: `${migSummary.estimatedMonthsSequential} month(s)` });
    }
    if (migSummary.estimatedMonthsParallel) {
      migItems.push({ label: 'Timeline (Parallel)', value: `${migSummary.estimatedMonthsParallel} month(s) with full team` });
    }
    sections.push({
      title: 'Migration Planning',
      icon: 'cpu',
      items: migItems,
    });
  }

  return {
    summary: `This application has been automatically analyzed for migration readiness. The analysis examined ${summary.totalForms || 0} forms, ${summary.totalViews || 0} views, and ${summary.totalAgents || 0} agents across ${summary.totalElements || 0} design elements.`,
    complexity: summary.complexityLabel || 'Unknown',
    estimatedEffort: summary.estimatedEffortWeeks ? `${summary.estimatedEffortWeeks} week(s)` : 'N/A',
    sections,
    recommendation: summary.hasWorkflows
      ? `This application includes workflow logic that will need to be migrated. Overall complexity is ${(summary.complexityLabel || 'unknown').toLowerCase()} with an estimated ${summary.estimatedEffortDays || 0} days of effort. We recommend a phased migration approach.`
      : `Overall complexity is ${(summary.complexityLabel || 'unknown').toLowerCase()} with an estimated ${summary.estimatedEffortDays || 0} days of effort. This application appears suitable for migration to the target platform.`,
  };
}

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showReport, setShowReport] = useState(false);

  const remaining = Math.max(0, (user.submissionLimit || 1) - (user.submissionsUsed || 0));

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line
  }, []);

  const fetchSubmissions = async () => {
    try {
      const res = await fetch('/api/submissions');
      if (!res.ok) throw new Error('Failed to fetch submissions.');
      const data = await res.json();
      setSubmissions(data.submissions);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (id) => {
    try {
      const res = await fetch(`/api/submissions/${id}/report`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'migration-report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleRequestReport = async (id) => {
    try {
      const res = await fetch(`/api/submissions/${id}/request-report`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(data.message, 'success');
      fetchSubmissions();
      // Also update selected submission if open
      if (selectedSubmission && selectedSubmission._id === id) {
        setSelectedSubmission({ ...selectedSubmission, formalReportRequested: true, formalReportRequestedAt: new Date().toISOString() });
      }
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }) + ' at ' + d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Check if report is visible to client (must be published)
  const isReportVisible = (sub) => {
    return sub.reportStatus === 'published' && sub.reportFilePath;
  };

  // Report-ready statuses — analysis & report actions should only appear once the report pipeline is reached
  const REPORT_READY_STATUSES = ['Report Ready', 'Report Sent', 'Completed'];
  const isReportReady = (sub) => REPORT_READY_STATUSES.includes(sub.status);

  // Statuses where the submission is still being processed (no analysis/report buttons yet)
  const PENDING_STATUSES = {
    Submitted: 'Pending Review — Your submission has been received and is awaiting review by our team.',
    'In Review': 'In Review — Our team is currently reviewing your submission.',
    Analyzing: 'Analyzing — Your application is being analyzed for migration readiness.',
  };

  if (loading) {
    return (
      <div className="page-loader">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            Dashboard
          </h1>
          <p className="dashboard-subtitle">
            Welcome back, <strong>{user.fullName}</strong> &mdash; {user.organizationName}
          </p>
        </div>
        {isAdmin && (
          <div className="dashboard-actions">
            <Link to="/admin" className="btn btn-admin">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Admin Panel
            </Link>
          </div>
        )}
      </div>

      {/* Submission Quota + Submit Actions */}
      <div className="quota-actions-row">
        <div className="quota-stats">
          <div className="quota-stat">
            <span className="quota-stat-value">{remaining}</span>
            <span className="quota-stat-label">Remaining</span>
          </div>
          <div className="quota-stat-divider" />
          <div className="quota-stat">
            <span className="quota-stat-value">{user.submissionsUsed || 0}</span>
            <span className="quota-stat-label">Used</span>
          </div>
          <div className="quota-stat-divider" />
          <div className="quota-stat">
            <span className="quota-stat-value">{user.submissionLimit || 1}</span>
            <span className="quota-stat-label">Total</span>
          </div>
        </div>
        <div className="dashboard-submit-actions">
          <Link to="/submit" className="btn btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
            Single App
          </Link>
          <Link to="/mass-import" className="btn btn-outline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
            Mass Import
          </Link>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-illustration" aria-hidden="true">
            <div className="empty-state-orb empty-state-orb-left" />
            <div className="empty-state-orb empty-state-orb-right" />
            <svg width="88" height="88" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
              <path d="M16 21H8" />
            </svg>
          </div>
          <h3>No files uploaded yet</h3>
          <p>Your dashboard is ready. Upload your first DXL file or ZIP portfolio to start your migration analysis.</p>
          <div className="empty-state-actions">
            <Link to="/submit" className="btn btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
              Single App Submission
            </Link>
            <Link to="/mass-import" className="btn btn-outline">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              Mass Import
            </Link>
          </div>
        </div>
      ) : (
        <div className="submissions-table-wrapper">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Type</th>
                <th>Platform Target</th>
                <th>Priority</th>
                <th>Date Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((sub) => (
                <tr key={sub._id}>
                  <td className="td-app-name">
                    <div className="td-app-name-inner">
                      {sub.submissionType === 'mass-import' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      )}
                      <button className="link-button" onClick={() => setSelectedSubmission(sub)}>
                        {sub.applicationName}
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`type-badge ${sub.submissionType === 'mass-import' ? 'type-mass' : 'type-single'}`}>
                      {sub.submissionType === 'mass-import' ? 'Portfolio' : 'Single'}
                    </span>
                  </td>
                  <td>{sub.targetPlatform}</td>
                  <td>
                    <span className={`priority-badge priority-${sub.priority.toLowerCase()}`}>
                      {sub.priority}
                    </span>
                  </td>
                  <td>{formatDate(sub.createdAt)}</td>
                  <td>
                    {sub.analysisStatus === 'failed' ? (
                      <span className="status-badge" style={{ background: '#fecaca', color: '#991b1b' }} title={sub.analysisError || 'Analysis failed'}>
                        Analysis Failed
                      </span>
                    ) : sub.analysisStatus === 'complete' ? (
                      <span className={`status-badge ${STATUS_STYLES[sub.status] || 'status-analyzing'}`}>
                        {sub.status}
                      </span>
                    ) : (sub.analysisStatus === 'analyzing' || sub.analysisStatus === 'pending') ? (
                      <span className="status-badge status-analyzing">
                        Analyzing...
                      </span>
                    ) : (
                      <span className={`status-badge ${STATUS_STYLES[sub.status]}`}>
                        {sub.status}
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <button className="btn btn-sm btn-outline" onClick={() => { setSelectedSubmission(sub); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        View
                      </button>
                      {isReportVisible(sub) && (
                        <button className="btn btn-sm btn-primary" onClick={() => handleDownloadReport(sub._id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          Report
                        </button>
                      )}
                      {sub.formalReportRequested && !isReportVisible(sub) && (
                        <span className="report-requested-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          Report Requested
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Submission Detail Dialog */}
      {selectedSubmission && !showReport && (
        <div className="dialog-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="dialog dialog-lg" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">
                {selectedSubmission.submissionType === 'mass-import' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                )}
                {selectedSubmission.applicationName}
              </h3>
              <button className="dialog-close-btn" onClick={() => setSelectedSubmission(null)}>&times;</button>
            </div>
            <div className="dialog-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">{DetailIcons.organization} Organization</span>
                  <span className="detail-value">{selectedSubmission.organizationName}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{DetailIcons.contact} Contact</span>
                  <span className="detail-value">{selectedSubmission.contactName}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{DetailIcons.email} Email</span>
                  <span className="detail-value">{selectedSubmission.contactEmail}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{DetailIcons.platform} Target Platform</span>
                  <span className="detail-value">{selectedSubmission.targetPlatform}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{DetailIcons.priority} Priority</span>
                  <span className="detail-value">
                    <span className={`priority-badge priority-${selectedSubmission.priority.toLowerCase()}`}>
                      {selectedSubmission.priority}
                    </span>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{DetailIcons.status} Status</span>
                  <span className="detail-value">
                    {selectedSubmission.analysisStatus === 'failed' ? (
                      <span className="status-badge" style={{ background: '#fecaca', color: '#991b1b' }} title={selectedSubmission.analysisError || 'Analysis failed'}>
                        Analysis Failed
                      </span>
                    ) : selectedSubmission.analysisStatus === 'complete' ? (
                      <span className={`status-badge ${STATUS_STYLES[selectedSubmission.status] || 'status-analyzing'}`}>
                        {selectedSubmission.status}
                      </span>
                    ) : (selectedSubmission.analysisStatus === 'analyzing' || selectedSubmission.analysisStatus === 'pending') ? (
                      <span className="status-badge status-analyzing">
                        Analyzing...
                      </span>
                    ) : (
                      <span className={`status-badge ${STATUS_STYLES[selectedSubmission.status]}`}>
                        {selectedSubmission.status}
                      </span>
                    )}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{DetailIcons.calendar} Submitted On</span>
                  <span className="detail-value">{formatDateTime(selectedSubmission.createdAt)}</span>
                </div>
                {selectedSubmission.submissionType !== 'mass-import' && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">{DetailIcons.users} Approx. Users</span>
                      <span className="detail-value">{selectedSubmission.approximateUsers}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{DetailIcons.documents} Approx. Documents</span>
                      <span className="detail-value">{selectedSubmission.approximateDocuments}</span>
                    </div>
                  </>
                )}
                {selectedSubmission.submissionType === 'mass-import' && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">{DetailIcons.apps} Total Applications</span>
                      <span className="detail-value">{selectedSubmission.totalApplications}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{DetailIcons.approach} Migration Approach</span>
                      <span className="detail-value" style={{textTransform: 'capitalize'}}>{(selectedSubmission.migrationApproach || '').replace(/-/g, ' ')}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="detail-section">
                <h4>{DetailIcons.description} Description</h4>
                <p>{selectedSubmission.applicationDescription || selectedSubmission.portfolioDescription}</p>
              </div>

              {selectedSubmission.submissionType !== 'mass-import' && (
                <div className="detail-section">
                  <h4>{DetailIcons.features} Application Features</h4>
                  <div className="feature-tags">
                    {selectedSubmission.hasWorkflowApprovals && <span className="feature-tag">Workflow Approvals</span>}
                    {selectedSubmission.hasEmailNotifications && <span className="feature-tag">Email Notifications</span>}
                    {selectedSubmission.hasIntegrations && <span className="feature-tag">Integrations</span>}
                    {selectedSubmission.hasScheduledAgents && <span className="feature-tag">Scheduled Agents</span>}
                    {selectedSubmission.hasCustomSecurity && <span className="feature-tag">Custom Security</span>}
                    {!selectedSubmission.hasWorkflowApprovals && !selectedSubmission.hasEmailNotifications && !selectedSubmission.hasIntegrations && !selectedSubmission.hasScheduledAgents && !selectedSubmission.hasCustomSecurity && (
                      <span className="text-muted">None selected</span>
                    )}
                  </div>
                </div>
              )}

              {selectedSubmission.submissionType === 'mass-import' && (
                <>
                  <div className="detail-section">
                    <h4>{DetailIcons.infrastructure} Infrastructure</h4>
                    <div className="feature-tags">
                      <span className="feature-tag">DB: {selectedSubmission.sharedDatabase}</span>
                      <span className="feature-tag">UI Apps: {selectedSubmission.allUiApps}</span>
                      <span className="feature-tag">Team: {selectedSubmission.hasMigrationTeam}</span>
                    </div>
                  </div>
                </>
              )}

              {selectedSubmission.integrationDetails && (
                <div className="detail-section">
                  <h4>{DetailIcons.integration} Integration Details</h4>
                  <p>{selectedSubmission.integrationDetails}</p>
                </div>
              )}
              {selectedSubmission.securityDetails && (
                <div className="detail-section">
                  <h4>{DetailIcons.security} Security Details</h4>
                  <p>{selectedSubmission.securityDetails}</p>
                </div>
              )}
              {selectedSubmission.additionalNotes && (
                <div className="detail-section">
                  <h4>{DetailIcons.notes} Additional Notes</h4>
                  <p>{selectedSubmission.additionalNotes}</p>
                </div>
              )}
            </div>
            {/* Status Info & Formal Report Section */}
            {PENDING_STATUSES[selectedSubmission.status] ? (
              /* Still being processed — show status-aware message */
              <div className={`submission-status-pill ${STATUS_STYLES[selectedSubmission.status]}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', borderRadius: '99px', marginTop: '0.75rem', fontSize: '0.85rem', fontWeight: 600 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {PENDING_STATUSES[selectedSubmission.status]}
              </div>
            ) : selectedSubmission.formalReportRequested ? (
              <div className="detail-section" style={{ marginTop: '0.5rem' }}>
                <div className="report-request-status">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Formal report requested on {formatDate(selectedSubmission.formalReportRequestedAt)}</span>
                </div>
              </div>
            ) : isReportReady(selectedSubmission) && !isReportVisible(selectedSubmission) ? (
              <div className="detail-section" style={{ marginTop: '0.5rem' }}>
                <div className="report-request-prompt">
                  <p>Need a detailed formal report? Our team will prepare a comprehensive migration analysis document for your records.</p>
                </div>
              </div>
            ) : null}

            {/* Analysis status indicator */}
            {selectedSubmission.analysisStatus === 'failed' && (
              <div style={{ marginTop: '0.75rem', padding: '0.65rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: '#dc2626', fontSize: '0.85rem' }}>Analysis Failed</span>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: '#7f1d1d' }}>
                    {selectedSubmission.analysisError || 'An error occurred during automated analysis. Our team has been notified.'}
                  </p>
                </div>
              </div>
            )}
            {(selectedSubmission.analysisStatus === 'analyzing' || selectedSubmission.analysisStatus === 'pending') && (
              <div style={{ marginTop: '0.75rem', padding: '0.65rem 1rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span style={{ fontWeight: 600, color: '#2563eb', fontSize: '0.85rem' }}>Analysis in progress — results will be available shortly</span>
              </div>
            )}

            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => setSelectedSubmission(null)}>Close</button>
              {(isReportReady(selectedSubmission) || selectedSubmission.analysisStatus === 'complete') && (
                <button className="btn btn-success" onClick={() => { setSelectedSubmission(null); navigate(`/analysis/${selectedSubmission._id}`); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  View Full Analysis
                </button>
              )}
              {isReportReady(selectedSubmission) && !selectedSubmission.formalReportRequested && !isReportVisible(selectedSubmission) && (
                <button className="btn btn-primary" onClick={() => handleRequestReport(selectedSubmission._id)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                  Request Formal Report
                </button>
              )}
              {isReportVisible(selectedSubmission) && (
                <button className="btn btn-primary" onClick={() => handleDownloadReport(selectedSubmission._id)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download Report
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analysis Report Dialog */}
      {showReport && selectedSubmission && (() => {
        const reportData = selectedSubmission.analysisStatus === 'complete' && selectedSubmission.analysisSummary
          ? buildReportFromAnalysis(selectedSubmission)
          : SAMPLE_REPORT;
        return (
        <div className="dialog-overlay" onClick={() => setShowReport(false)}>
          <div className="dialog dialog-xl" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                Migration Analysis — {selectedSubmission.applicationName || selectedSubmission.portfolioName}
              </h3>
              <button className="dialog-close-btn" onClick={() => setShowReport(false)}>&times;</button>
            </div>
            <div className="dialog-body">
              <div className="report-summary-bar">
                <div className="report-stat">
                  <span className="report-stat-label">Complexity</span>
                  <span className="report-stat-value report-stat-warning">{reportData.complexity}</span>
                </div>
                <div className="report-stat">
                  <span className="report-stat-label">Est. Effort</span>
                  <span className="report-stat-value">{reportData.estimatedEffort}</span>
                </div>
                <div className="report-stat">
                  <span className="report-stat-label">Status</span>
                  <span className="report-stat-value report-stat-success">Analysis Complete</span>
                </div>
              </div>

              <p className="report-intro">{reportData.summary}</p>

              {reportData.sections.map((section, i) => (
                <div key={i} className="report-section">
                  <h4 className="report-section-title">
                    {getSectionIcon(section.icon)}
                    {section.title}
                  </h4>
                  <div className="report-items">
                    {section.items.map((item, j) => (
                      <div key={j} className="report-item">
                        <span className="report-item-label">{item.label}</span>
                        <span className="report-item-value">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="report-recommendation">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem', verticalAlign: 'middle'}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Recommendation
                </h4>
                <p>{reportData.recommendation}</p>
              </div>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => { setShowReport(false); }}>Back to Details</button>
              <button className="btn btn-success" onClick={() => { setShowReport(false); setSelectedSubmission(null); }}>Done</button>
            </div>
          </div>
        </div>
        );
      })()}

    </div>
  );
}
